/**
 * Boiling Point Calculator Web Component
 * Author: Ben Quackenbush
 * Date: 2/13/2026
 * Description: This component calculates the boiling point of water based on user input or geolocation.
 * It uses the Mapbox API for geocoding and elevation data, and the OpenWeather API via proxy for current pressure data.
 */

import { Component } from '@theme/component';
class TwBoilingPointCalc extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.mapboxToken = this.dataset.mapboxToken;
    this.map = null;
    this.marker = null;

    // Unit state
    this.distanceUnit = 'ft'; // ft or m
    this.pressureUnit = 'inHg'; // inHg, hPa, kPa

    // Internal storage in base units (meters, hPa, Celsius)
    this.elevation = 0; // meters
    this.groundPressure = 1013.25; // hPa (sea level)
    this.stationPressure = 1013.25; // hPa
    this.boilingPoint = 100; // Celsius
    this.location = '';

    this.initEventListeners();
    this.updateResultsDisplay();
    this.initializeMap();
  }

  initEventListeners() {
    // Location lookup button
    const locationBtn = this.querySelector('[data-location-lookup]');
    if (locationBtn) {
      locationBtn.addEventListener('click', this.handleLocationLookup);
    }

    // Use current location button
    const geoBtn = this.querySelector('[data-use-geolocation]');
    if (geoBtn) {
      geoBtn.addEventListener('click', this.handleGeolocation);
    }

    // Editable field inputs
    const elevationInput = this.querySelector('[data-input-elevation]');
    if (elevationInput) {
      elevationInput.addEventListener('input', this.handleElevationChange);
    }

    const groundPressureInput = this.querySelector(
      '[data-input-ground-pressure]',
    );
    if (groundPressureInput) {
      groundPressureInput.addEventListener(
        'input',
        this.handleGroundPressureChange,
      );
    }

    const stationPressureInput = this.querySelector(
      '[data-input-station-pressure]',
    );
    if (stationPressureInput) {
      stationPressureInput.addEventListener(
        'input',
        this.handleStationPressureChange,
      );
    }

    // Unit toggles
    const distanceToggle = this.querySelector('[data-distance-toggle]');
    if (distanceToggle) {
      distanceToggle.addEventListener('change', (e) => {
        this.distanceUnit = e.target.value;
        this.updateResultsDisplay();
      });
    }

    const pressureToggle = this.querySelector('[data-pressure-toggle]');
    if (pressureToggle) {
      pressureToggle.addEventListener('change', (e) => {
        this.pressureUnit = e.target.value;
        this.updateResultsDisplay();
      });
    }
  }

  handleLocationLookup = async (e) => {
    e.preventDefault();
    const locationInput = this.querySelector('[data-location-input]');
    const location = locationInput?.value?.trim();

    if (!location) {
      this.showError('Please enter a location');
      return;
    }

    this.showLoading(true);
    this.clearError();

    try {
      // Geocode location with Mapbox
      const geoData = await this.geocodeLocation(location);
      if (!geoData) {
        this.showError('Location not found');
        this.showLoading(false);
        return;
      }

      const { lat, lon, displayName } = geoData;

      // Fetch weather data from OpenWeather One Call
      const weatherData = await this.fetchWeatherData(lat, lon);
      if (!weatherData) {
        this.showError('Unable to fetch weather data');
        this.showLoading(false);
        return;
      }
      const { pressure } = weatherData;
      // Fetch elevation from Mapbox Terrain
      const elevation = await this.fetchElevationFromMapbox(lat, lon);

      // Store in internal state
      this.elevation = elevation;
      this.stationPressure = pressure;
      this.groundPressure = this.calculateGroundPressure(pressure, elevation);
      this.boilingPoint = this.calculateBoilingPoint(this.groundPressure);
      this.location = displayName;
      this.lat = lat;
      this.lon = lon;

      // Update UI
      this.updateResultsDisplay();
      this.updateMap(lat, lon, displayName);

      this.showLoading(false);
    } catch (error) {
      console.error('Location lookup error:', error);
      this.showError('An error occurred during lookup');
      this.showLoading(false);
    }
  };

  handleGeolocation = async (e) => {
    e.preventDefault();
    if (!navigator.geolocation) {
      this.showError('Geolocation is not supported by your browser');
      return;
    }

    this.showLoading(true);
    this.clearError();

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;

        try {
          // Reverse geocode to get location name
          const displayName = await this.reverseGeocode(lat, lon);

          // Fetch weather data
          const weatherData = await this.fetchWeatherData(lat, lon);
          if (!weatherData) {
            this.showError('Unable to fetch weather data');
            this.showLoading(false);
            return;
          }
          const { pressure } = weatherData;

          // Fetch elevation
          const elevation = await this.fetchElevationFromMapbox(lat, lon);

          // Store in internal state
          this.elevation = elevation;
          this.stationPressure = pressure;
          this.groundPressure = this.calculateGroundPressure(
            pressure,
            elevation,
          );
          this.boilingPoint = this.calculateBoilingPoint(this.groundPressure);
          this.location = displayName || 'Current Location';
          this.lat = lat;
          this.lon = lon;

          // Update UI
          this.updateResultsDisplay();
          this.updateMap(lat, lon, displayName || 'Current Location');

          this.showLoading(false);
        } catch (error) {
          console.error('Geolocation processing error:', error);
          this.showError('An error occurred processing your location');
          this.showLoading(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        this.showError(
          'Unable to access your location. Please check permissions.',
        );
        this.showLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  handleElevationChange = () => {
    const elevationInput = this.querySelector('[data-input-elevation]');
    const value = parseFloat(elevationInput?.value || 0);

    if (isNaN(value)) return;

    // Convert to meters if needed
    this.elevation = this.distanceUnit === 'ft' ? value * 0.3048 : value;

    // Recalculate ground pressure from station pressure and elevation
    this.groundPressure = this.calculateGroundPressure(
      this.stationPressure,
      this.elevation,
    );
    this.boilingPoint = this.calculateBoilingPoint(this.groundPressure);

    this.updateResultsDisplay();
  };

  handleGroundPressureChange = () => {
    const groundInput = this.querySelector('[data-input-ground-pressure]');
    const value = parseFloat(groundInput?.value || 0);

    if (isNaN(value) || value <= 0) return;

    // Convert to hPa if needed
    this.groundPressure = this.convertPressureToHPa(value, this.pressureUnit);

    // Recalculate station pressure from ground pressure and elevation
    this.stationPressure = this.adjustPressureForElevation(
      this.groundPressure,
      this.elevation,
    );
    this.boilingPoint = this.calculateBoilingPoint(this.groundPressure);

    this.updateResultsDisplay();
  };

  handleStationPressureChange = () => {
    const stationInput = this.querySelector('[data-input-station-pressure]');
    const value = parseFloat(stationInput?.value || 0);

    if (isNaN(value) || value <= 0) return;

    // Convert to hPa if needed
    this.stationPressure = this.convertPressureToHPa(value, this.pressureUnit);

    // Recalculate ground pressure from station pressure and elevation
    this.groundPressure = this.calculateGroundPressure(
      this.stationPressure,
      this.elevation,
    );
    this.boilingPoint = this.calculateBoilingPoint(this.groundPressure);

    this.updateResultsDisplay();
  };

  async geocodeLocation(location) {
    if (!this.mapboxToken) {
      console.error('Mapbox token not configured');
      return null;
    }

    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(location)}.json?access_token=${this.mapboxToken}&limit=1`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        return {
          lat: feature.center[1],
          lon: feature.center[0],
          displayName: feature.place_name,
        };
      }

      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  }

  async reverseGeocode(lat, lon) {
    if (!this.mapboxToken) {
      console.error('Mapbox token not configured');
      return null;
    }

    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=${this.mapboxToken}&limit=1`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        return data.features[0].place_name;
      }
      return null;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return null;
    }
  }

  async fetchWeatherData(lat, lon) {
    try {
      const url = `https://api-rl5tteg2ja-uc.a.run.app/open-weather?lat=${lat}&lon=${lon}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.main) {
        return {
          pressure: data.main.pressure, // hPa
          seaLevelPressure: data.main.sea_level, // hPa
          groundLevelPressure: data.main.grnd_level, // hPa
        };
      }

      return null;
    } catch (error) {
      console.error('Weather API error:', error);
      return null;
    }
  }

  async fetchElevationFromMapbox(lat, lon) {
    if (!this.mapboxToken) {
      console.error('Mapbox token not configured');
      return 0;
    }

    try {
      const url = `https://api.mapbox.com/v4/mapbox.mapbox-terrain-v2/tilequery/${lon},${lat}.json?layers=contour&access_token=${this.mapboxToken}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const withEle = data.features.find(
          (feature) => feature.properties && feature.properties.ele != null,
        );
        if (withEle) {
          return Number(withEle.properties.ele);
        }
      }
      return 0;
    } catch (error) {
      console.error('Mapbox elevation error:', error);
      return 0;
    }
  }

  adjustPressureForElevation(groundPressurehPa, elevationMeters) {
    if (!groundPressurehPa || !elevationMeters) return groundPressurehPa;

    const standardTempK = 288.15; // We assume 15°C in Kelvin
    const lapseRate = 0.0065; // We assume the lapseRate as well, K/m
    const elevationM = elevationMeters;
    const stationPressure =
      groundPressurehPa /
      Math.pow(1 - (lapseRate * elevationM) / standardTempK, 5.255);
    return stationPressure;
  }

  convertTemp(tempC, unit) {
    if (unit === 'F') {
      return (tempC * 9) / 5 + 32;
    }
    return tempC;
  }

  convertPressure(pressureHPa, unit) {
    switch (unit) {
      case 'inHg':
        return pressureHPa * 0.02953;
      case 'kPa':
        return pressureHPa / 10;
      case 'hPa':
      default:
        return pressureHPa;
    }
  }

  convertPressureToHPa(value, unit) {
    switch (unit) {
      case 'inHg':
        return value / 0.02953;
      case 'kPa':
        return value * 10;
      case 'hPa':
      default:
        return value;
    }
  }

  convertDistance(meters, unit) {
    return unit === 'ft' ? meters * 3.28084 : meters;
  }

  convertDistanceToMeters(value, unit) {
    return unit === 'ft' ? value * 0.3048 : value;
  }

  calculateGroundPressure(stationPressurehPa, elevationMeters) {
    if (!elevationMeters) return stationPressurehPa;
    // Calculate the actual local pressure at elevation (Station Pressure)
    // Using the barometric formula: P = P0 * (1 - L*h / T0)^(g*M / R*L)
    const standardTempK = 288.15; // We assume 15°C in Kelvin
    const lapseRate = 0.0065; // We assume the lapseRate as well, K/m
    const elevationM = elevationMeters;

    const groundPressure =
      stationPressurehPa *
      Math.pow(1 - (lapseRate * elevationM) / standardTempK, 5.255);
    return groundPressure;
  }

  calculateBoilingPoint(groundPressurehPa) {
    // Calculate Boiling Point using the Antoine Equation for water
    // log10(P) = A - (B / (T + C)) where P is in mmHg
    const mmHg = groundPressurehPa * 0.750062;
    const A = 8.07131;
    const B = 1730.63;
    const C = 233.426;

    const tempC = B / (A - Math.log10(mmHg)) - C;
    return tempC;
  }

  initializeMap() {
    const mapContainer = this.querySelector('[data-map-container]');
    if (!mapContainer || !this.mapboxToken) return;

    if (window.mapboxgl) {
      const usCenter = { lat: 39.8283, lon: -98.5795 };
      mapboxgl.accessToken = this.mapboxToken;
      this.map = new mapboxgl.Map({
        container: mapContainer,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [usCenter.lon, usCenter.lat],
        zoom: 3.5,
      });

      // Add navigation controls
      this.map.addControl(new mapboxgl.NavigationControl());
      this.map.on('load', () => {
        this.map.resize();
      });

      // Add click-to-drop-pin functionality
      this.map.on('click', async (e) => {
        const clickedLat = e.lngLat.lat;
        const clickedLon = e.lngLat.lng;

        this.showLoading(true);
        this.clearError();

        try {
          // Reverse geocode to get location name
          const newDisplayName = await this.reverseGeocode(
            clickedLat,
            clickedLon,
          );

          // Fetch weather data
          const weatherData = await this.fetchWeatherData(
            clickedLat,
            clickedLon,
          );
          if (!weatherData) {
            this.showError('Unable to fetch weather data');
            this.showLoading(false);
            return;
          }
          const { pressure } = weatherData;

          // Fetch elevation
          const elevation = await this.fetchElevationFromMapbox(
            clickedLat,
            clickedLon,
          );

          // Store in internal state
          this.elevation = elevation;
          this.stationPressure = pressure;
          this.groundPressure = this.calculateGroundPressure(
            pressure,
            elevation,
          );
          this.boilingPoint = this.calculateBoilingPoint(this.groundPressure);
          this.location =
            newDisplayName ||
            `${clickedLat.toFixed(4)}, ${clickedLon.toFixed(4)}`;
          this.lat = clickedLat;
          this.lon = clickedLon;

          // Update results display
          this.updateResultsDisplay();

          // Update marker to new location
          if (this.marker) {
            this.marker.remove();
          }
          this.marker = new mapboxgl.Marker()
            .setLngLat([clickedLon, clickedLat])
            .setPopup(
              new mapboxgl.Popup().setHTML(`<strong>${this.location}</strong>`),
            )
            .addTo(this.map);

          this.marker.togglePopup();
          this.map.flyTo({ center: [clickedLon, clickedLat], zoom: 10 });

          this.showLoading(false);
        } catch (error) {
          console.error('Map click processing error:', error);
          this.showError('An error occurred processing the clicked location');
          this.showLoading(false);
        }
      });
    }
  }

  updateMap(lat, lon, displayName) {
    const mapContainer = this.querySelector('[data-map-container]');
    if (!mapContainer || !this.mapboxToken) return;
    // Initialize map if not already done
    if (!this.map) {
      this.initializeMap();
    }

    // Update map center and marker
    if (this.map) {
      // Remove old marker
      if (this.marker) {
        this.marker.remove();
      }
      // Add new marker
      this.marker = new mapboxgl.Marker()
        .setLngLat([lon, lat])
        .setPopup(
          new mapboxgl.Popup({ focusAfterOpen: false }).setHTML(
            `<strong>${displayName}</strong>`,
          ),
        )
        .addTo(this.map);
      this.marker.togglePopup();
      this.map.flyTo({ center: [lon, lat], zoom: 10 });
    }
  }

  updateResultsDisplay() {
    // Show both F and C for boiling point
    const bpF = this.convertTemp(this.boilingPoint, 'F');
    const bpC = this.boilingPoint;
    const resultBP = this.querySelector('[data-result-boiling-point]');
    const bpContainer = this.querySelector(
      '[data-result-boiling-point-container]',
    );

    if (resultBP) {
      resultBP.textContent = `${bpF.toFixed(2)}°F / ${bpC.toFixed(2)}°C`;
    }
    if (bpContainer) {
      // Trigger animation by toggling a class
      bpContainer.classList.remove('bp-result-animate');
      void bpContainer.offsetWidth;
      bpContainer.classList.add('bp-result-animate');
    }
    // Update ground pressure input
    const groundInput = this.querySelector('[data-input-ground-pressure]');
    if (groundInput && document.activeElement !== groundInput) {
      const displayGround = this.convertPressure(
        this.groundPressure,
        this.pressureUnit,
      );
      groundInput.value = displayGround.toFixed(2);
    }

    // Update station pressure input
    const stationInput = this.querySelector('[data-input-station-pressure]');
    if (stationInput && document.activeElement !== stationInput) {
      const displayStation = this.convertPressure(
        this.stationPressure,
        this.pressureUnit,
      );
      stationInput.value = displayStation.toFixed(2);
    }
    const distanceUnitLabel = this.querySelectorAll('.distance-unit');
    distanceUnitLabel.forEach((el) => {
      el.textContent = `(${this.distanceUnit})`;
    });

    const pressureUnitLabel = this.querySelectorAll('.pressure-unit');
    pressureUnitLabel.forEach((el) => {
      el.textContent = `(${this.pressureUnit})`;
    });
    const elevationInput = this.querySelector('[data-input-elevation]');
    if (elevationInput && document.activeElement !== elevationInput) {
      const displayElevation = this.convertDistance(
        this.elevation,
        this.distanceUnit,
      );
      elevationInput.value = displayElevation.toFixed(
        this.distanceUnit === 'ft' ? 0 : 1,
      );
    }

    // Update location display
    const locationDisplay = this.querySelector('[data-result-location]');
    if (locationDisplay && this.location) {
      locationDisplay.textContent = this.location;
    }

    if (this.map) {
      this.map.resize();
    }
  }

  showLoading(isLoading) {
    const loader = this.querySelector('[data-loading]');
    if (loader) {
      loader.classList.toggle('hidden', !isLoading);
    }

    const locationBtn = this.querySelector('[data-location-lookup]');
    if (locationBtn) {
      locationBtn.disabled = isLoading;
      locationBtn.style.display = isLoading ? 'none' : '';
    }
    const geoBtn = this.querySelector('[data-use-geolocation]');
    if (geoBtn) {
      geoBtn.disabled = isLoading;
      geoBtn.style.display = isLoading ? 'none' : '';
    }
  }

  showError(message) {
    const errorEl = this.querySelector('[data-error]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.remove('hidden');
    }
  }

  clearError() {
    const errorEl = this.querySelector('[data-error]');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.classList.add('hidden');
    }
  }
}

if (!customElements.get('tw-boiling-point-calc')) {
  customElements.define('tw-boiling-point-calc', TwBoilingPointCalc);
}
